// Modules
import { useContext, useCallback, useEffect, useRef } from 'react';
import { SocketContext, SocketContextValue } from 'contexts/SocketContext';

/**
 * Hook to access the shared socket connection
 * 
 * @example
 * const { socket, isConnected, addListener, removeListener, emit } = useSocket();
 * 
 * useEffect(() => {
 *   const handleNewMessage = (data: Message) => {
 *     console.log('New message:', data);
 *   };
 *   
 *   addListener('new_message', handleNewMessage);
 *   
 *   return () => {
 *     removeListener('new_message', handleNewMessage);
 *   };
 * }, [addListener, removeListener]);
 */
export const useSocket = (): SocketContextValue => {
  const context = useContext(SocketContext);

  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }

  return context;
};

/**
 * Hook for subscribing to socket events with automatic cleanup
 * 
 * @param event - The event name to listen to
 * @param callback - The callback to execute when the event is received
 * @param deps - Dependencies array for the callback (optional)
 * 
 * @example
 * useSocketEvent('new_notification', (notification: Notification) => {
 *   toast.success(notification.message);
 * });
 */
export const useSocketEvent = <T>(
  event: string,
  callback: (data: T) => void,
  deps: unknown[] = [],
): void => {
  const { addListener, removeListener, isConnected } = useSocket();
  const callbackRef = useRef(callback);

  // Update callback ref when it changes
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Stable callback wrapper
  const stableCallback = useCallback((data: T) => {
    callbackRef.current(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    if (!isConnected) return;

    addListener(event, stableCallback);

    return () => {
      removeListener(event, stableCallback as (...args: unknown[]) => void);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event, isConnected, addListener, removeListener, ...deps]);
};

export default useSocket;

